import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

// Rotating carousel for Matchbook affiliate banners.
// - Auto-rotates every `intervalMs` (default 3s) with a smooth fade transition.
// - Pauses rotation while the user hovers a slide (so they can click it).
// - Fires each banner's impression pixel exactly once, the first time it
//   becomes both visible in the viewport AND the active slide.
export default function MatchbookCarousel({
  banners = [],
  intervalMs = 3000,
  label = "Featured Partner",
  testId = "matchbook-carousel",
  onActiveChange,
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const [fired, setFired] = useState(() => banners.map(() => false));
  const rootRef = useRef(null);

  // IntersectionObserver — only count impressions when banner is on-screen.
  // Re-attaches when banners arrive async (component renders null until then).
  useEffect(() => {
    if (!rootRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) setInView(e.isIntersecting);
      },
      { threshold: 0.25 }
    );
    io.observe(rootRef.current);
    return () => io.disconnect();
  }, [banners.length]);

  // Reset impression bookkeeping when the banner set changes.
  useEffect(() => {
    setFired(banners.map(() => false));
    setIndex(0);
  }, [banners.length]);

  // Rotation timer.
  useEffect(() => {
    if (paused || banners.length <= 1) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % banners.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [paused, banners.length, intervalMs]);

  // Notify parent whenever active slide changes.
  useEffect(() => {
    if (onActiveChange && banners[index]) onActiveChange(banners[index], index);
  }, [index, banners, onActiveChange]);

  // Fire impression pixel for the active slide once it's visible.
  useEffect(() => {
    if (!inView) return;
    const active = banners[index];
    if (!active || fired[index]) return;
    if (active.impressionUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = active.impressionUrl;
      iframe.setAttribute("aria-hidden", "true");
      iframe.setAttribute("tabindex", "-1");
      document.body.appendChild(iframe);
    }
    // Internal in-house tracking (so admin can see CTR per banner).
    if (active.id) {
      api.post(`/track/banner/${encodeURIComponent(active.id)}/impression`).catch(() => {});
    }
    setFired((prev) => {
      const next = [...prev];
      next[index] = true;
      return next;
    });
  }, [inView, index, banners, fired]);

  if (!banners.length) return null;

  return (
    <section data-testid={testId} className="mt-6 sb-fade-up">
      <div className="flex items-center justify-between mb-2 px-1">
        <h2
          className="font-mono text-[10px] uppercase tracking-[0.2em]"
          style={{ color: "var(--sb-text-muted)" }}
        >
          // {label}
        </h2>
        <span className="font-mono text-[10px]" style={{ color: "var(--sb-text-muted)" }}>
          18+ T&Cs
        </span>
      </div>

      <div
        ref={rootRef}
        className="relative sb-card rounded-lg overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
        data-testid={`${testId}-stage`}
      >
        {/* Aspect-ratio spacer (728x90 → 8.09:1 → 12.36% padding). */}
        <div style={{ paddingTop: "12.36%" }} aria-hidden="true" />

        {banners.map((b, i) => (
          <a
            key={b.clickUrl}
            href={b.clickUrl}
            target="_blank"
            rel="noopener noreferrer sponsored"
            data-testid={`${testId}-slide-${i}`}
            aria-hidden={i === index ? "false" : "true"}
            tabIndex={i === index ? 0 : -1}
            onClick={() => {
              if (b.id) api.post(`/track/banner/${encodeURIComponent(b.id)}/click`).catch(() => {});
            }}
            className="absolute inset-0 block transition-opacity duration-700 ease-in-out"
            style={{ opacity: i === index ? 1 : 0, pointerEvents: i === index ? "auto" : "none" }}
          >
            {b.customNode ? (
              b.customNode
            ) : (
              <img
                src={b.imgUrl}
                alt={b.alt || "Matchbook — Featured UK bookmaker offer"}
                className="block w-full h-full object-contain"
                loading={i === 0 ? "eager" : "lazy"}
              />
            )}
          </a>
        ))}

        {/* Dot indicators (subtle, mobile-friendly). */}
        {banners.length > 1 && (
          <div
            className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5"
            data-testid={`${testId}-dots`}
          >
            {banners.map((b, i) => (
              <button
                key={b.id || b.clickUrl || i}
                type="button"
                aria-label={`Show banner ${i + 1}`}
                onClick={() => setIndex(i)}
                data-testid={`${testId}-dot-${i}`}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: i === index ? 18 : 6,
                  background:
                    i === index ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.45)",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.4)",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
